import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Card } from "../../../components/Card";
import { SectionTitle } from "../../../components/SectionTitle";
import { Button } from "../../../components/Button";
import { FormActions } from "../../../components/FormActions";
import { InfoBox } from "../../../components/InfoBox";
import { DryRunMetadata } from "./DryRunMetadata";
import { ErrorsTable } from "./ErrorsTable";
import { ValidProductsTable } from "./ValidProductsTable";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import type { ImportDryRunResults } from "../../../api/productsApi";

const ResultsCard = styled(Card)`
  margin-top: ${spacing.x4}px;
`;

const SectionDescription = styled.p`
  margin: 0 0 ${spacing.x3}px 0;
  max-width: 52rem;
  font-size: 14px;
  line-height: 1.55;
  color: ${colors.textMuted};
`;

const StyledFormActions = styled(FormActions)`
  margin-top: ${spacing.x4}px;
`;

interface DryRunResultsProps {
  results: ImportDryRunResults;
  onImport: () => void;
  canImport: boolean;
  isImporting: boolean;
}

export const DryRunResults = ({
  results,
  onImport,
  canImport,
  isImporting,
}: DryRunResultsProps) => {
  const { t } = useTranslation();
  const errors = results.errors || [];
  const validProducts = results.validProducts || [];
  const hasErrors = errors.length > 0;

  return (
    <ResultsCard>
      <SectionTitle as="h3">{t("productImport.dryRunResultsTitle")}</SectionTitle>
      <SectionDescription>{t("productImport.dryRunResultsDescription")}</SectionDescription>
      <DryRunMetadata
        totalRows={results.totalRows}
        validRows={results.validRows}
        errorCount={results.errorCount}
        success={results.success}
      />

      {hasErrors ? (
        <ErrorsTable errors={errors} />
      ) : (
        <>
          <InfoBox variant="success">{t("productImport.dryRunSuccessNoErrors")}</InfoBox>
          <ValidProductsTable products={validProducts} />
        </>
      )}

      <StyledFormActions>
        <Button onClick={onImport} disabled={!canImport || isImporting}>
          {isImporting ? t("productImport.importing") : t("productImport.importProducts")}
        </Button>
      </StyledFormActions>
    </ResultsCard>
  );
};
